import { Component, inject, signal } from '@angular/core';
import { form, FormField, FormRoot, minLength, required, submit } from '@angular/forms/signals';
import { MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { passwordsMatch, passwordStrength } from '@app/shared/forms/rules';
import { AuthService } from '@app/shared/services/auth.service';
import { ChangePasswordRequestBody } from '@app/shared/types/models/auth/auth.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

interface ChangePasswordFormModel {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

@Component({
  selector: 'proffeo-change-user-password-dialog',
  imports: [UiDialogComponent, TranslatePipe, FormField, FormRoot, UiButtonComponent, MatDialogClose, UiInputComponent],
  templateUrl: './change-user-password-dialog.component.html'
})
export class ChangeUserPasswordDialogComponent {
  private readonly authService = inject(AuthService);
  private readonly dialogRef = inject(MatDialogRef<ChangeUserPasswordDialogComponent, boolean>);

  protected readonly model = signal<ChangePasswordFormModel>({ oldPassword: '', newPassword: '', confirmPassword: '' });
  protected readonly form = form(this.model, path => {
    required(path.oldPassword, { message: 'ui-forms-this-field-is-required' });
    required(path.newPassword, { message: 'ui-forms-this-field-is-required' });
    minLength(path.newPassword, 8, { message: 'password-validation-message' });
    passwordStrength(path.newPassword, { message: 'password-validation-message' });
    required(path.confirmPassword, { message: 'ui-forms-this-field-is-required' });
    passwordsMatch(path.confirmPassword, path.newPassword, { message: 'confirm-password-does-not-match-password' });
  });

  protected changePassword(): void {
    void submit(this.form, {
      action: async () => {
        try {
          await firstValueFrom(this.authService.changePassword(this.toRequest()), { defaultValue: undefined });
          this.dialogRef.close(true);
        } catch {
          // The HTTP error interceptor reports the failure; the dialog stays open so the user can correct it.
        }
      }
    });
  }

  private toRequest(): ChangePasswordRequestBody {
    const { oldPassword, newPassword } = this.model();
    return { password: oldPassword, newPassword };
  }
}
